/* eslint-disable @typescript-eslint/ban-types */

import useModal from '@ctn/ui/components/ctn-modal/hooks/useModal';
import { useEffect, useRef, useState, type ReactElement, type RefObject } from 'react';
import { generatePath, useNavigate, useSearchParams } from 'react-router-dom';

import ModalSubsanacionDocumentos from '@/components/ModalSubsanacionDocumentos';
import ModalSubsanacionInterviniente from '@/components/ModalSubsanacionInterviniente';
import SubsanacionesFilter, { type SubsanacionesFilterRef } from '@/components/SubsanacionesFilter';
import PageTabs from '@/containers/PageTabs';
import { useGlobalContext } from '@/context/global.context';
import { usePageContext } from '@/context/page.context';
import useRouteTramite from '@/hooks/useRouteTramite';

import { ESTADO_SUBSANACION, TIPO_SUBSANACION } from '@/constants/tramite.constants';
import { getBreadcrumbTramite } from '@/helpers/navigation';
import type { ISubsanacionDocumento, ISubsanacionSujeto } from '@/interfaces/model/don.interface';

import { ROUTES_LABEL } from '../../constants/routes.constants';

const TAB_ID = {
	pendientes: 'pendientes',
	atendidas: 'atendidas',
};

export const Pendientes = ({
	onSubsanar,
	onViewDetail,
	tableRef,
}: {
	readonly tableRef: RefObject<SubsanacionesFilterRef | null>;
	readonly onSubsanar: (rowData: ISubsanacionDocumento | ISubsanacionSujeto) => void;
	readonly onViewDetail: (rowData: ISubsanacionDocumento | ISubsanacionSujeto) => void;
}) => (
	<SubsanacionesFilter
		ref={tableRef}
		idEstado={ESTADO_SUBSANACION.PENDIENTE}
		onSubsanar={onSubsanar}
		onViewDetail={onViewDetail}
	/>
);

export const Atendidas = ({
	onViewDetail,
}: {
	readonly onViewDetail: (rowData: ISubsanacionDocumento | ISubsanacionSujeto) => void;
}) => <SubsanacionesFilter idEstado={ESTADO_SUBSANACION.SUBSANADA} onViewDetail={onViewDetail} />;

const TramiteSubsanaciones = (): ReactElement => {
	const navigate = useNavigate();
	const ROUTES_TRAMITE = useRouteTramite();
	const [searchParams] = useSearchParams();
	const tableRef = useRef<SubsanacionesFilterRef>(null);
	const tabParam = searchParams.get('tab');
	const modalDocumento = useModal({});
	const modalSujeto = useModal({});
	const [dataSubsanacionSujeto, setDataSubsanacionSujeto] = useState<ISubsanacionSujeto>();
	const [dataSubsanacionDocumento, setDataSubsanacionDocumento] = useState<ISubsanacionDocumento>();
	const { appendBreadcrumb, replaceLastBreadcrumb } = usePageContext();
	const { getTramiteData } = useGlobalContext();

	const onViewDetail = (rowData: ISubsanacionDocumento | ISubsanacionSujeto) => {
		const { tipo, tramite, id } = rowData;
		const path = generatePath(ROUTES_TRAMITE.SUBSANACIONES_DETALLE, {
			tab: location.pathname.includes('atendidas') ? 'atendidas' : 'pendientes',
			idTramite: tramite.id,
			idTipoSubsanacion: tipo.id,
			idSubsanacion: id,
		});
		return navigate(path);
	};

	const defaultActiveTab =
		tabParam && ROUTES_TRAMITE.SOLICITUDES_DOCUMENTACION_ATENDIDAS.includes(tabParam)
			? TAB_ID.atendidas
			: TAB_ID.pendientes;

	const isDocumento = (data: ISubsanacionSujeto | ISubsanacionDocumento): data is ISubsanacionDocumento =>
		(data as ISubsanacionDocumento).tipo.id === TIPO_SUBSANACION.DOCUMENTO;

	const onSubsanarDocumento = (rowData: ISubsanacionDocumento) => {
		modalDocumento.openModal();
		setDataSubsanacionDocumento(rowData);
	};

	const onSubsanarSujeto = (rowData: ISubsanacionSujeto) => {
		modalSujeto.openModal();
		setDataSubsanacionSujeto(rowData);
	};

	const onSubsanar = (rowData: ISubsanacionSujeto | ISubsanacionDocumento) =>
		isDocumento(rowData) ? onSubsanarDocumento(rowData) : onSubsanarSujeto(rowData);

	const onSubmitSubsanacionDocumento = () => {
		modalDocumento.closeModal();
		tableRef.current?.reload();
	};

	const onSubmitSubsanacionSujeto = () => {
		modalSujeto.closeModal();
		tableRef.current?.reload();
	};

	useEffect(() => {
		const breadcrumbTramite = getBreadcrumbTramite(getTramiteData());
		appendBreadcrumb(ROUTES_LABEL.SUBSANACIONES, [breadcrumbTramite, { label: ROUTES_LABEL.SUBSANACIONES, url: location.pathname }]);
	}, []);
	return (
		<>
			<PageTabs
				hasHelp
				title={ROUTES_LABEL.SUBSANACIONES}
				defaultActive={defaultActiveTab}
				tabs={[
					{
						key: TAB_ID.pendientes,
						header: 'Pendientes',
						content: <Pendientes tableRef={tableRef} onViewDetail={onViewDetail} onSubsanar={onSubsanar} />,
						assignUrl: ROUTES_TRAMITE.SUBSANACIONES_PENDIENTES,
					},
					{
						key: TAB_ID.atendidas,
						header: 'Atendidas',
						content: <Atendidas onViewDetail={onViewDetail} />,
						assignUrl: ROUTES_TRAMITE.SUBSANACIONES_ATENDIDAS,
					},
				]}
				onTabChange={(index) => replaceLastBreadcrumb(ROUTES_LABEL.SUBSANACIONES, index === 'pendientes' ? ROUTES_TRAMITE.SUBSANACIONES_PENDIENTES : ROUTES_TRAMITE.SUBSANACIONES_ATENDIDAS)}
			/>
			{dataSubsanacionDocumento && (
				<ModalSubsanacionDocumentos
					hasErrorDoc
					data={{
						idTramite: dataSubsanacionDocumento.tramite.id,
						id: dataSubsanacionDocumento.id,
						idTipo: dataSubsanacionDocumento.tipo.id,
					}}
					idTipoDocumento={dataSubsanacionDocumento.documentoErroneo.tipoDocumento.id}
					isOpen={modalDocumento.isShowing}
					onClose={modalDocumento.closeModal}
					onSubmit={onSubmitSubsanacionDocumento}
				/>
			)}
			{dataSubsanacionSujeto && (
				<ModalSubsanacionInterviniente
					data={{
						idTramite: dataSubsanacionSujeto.tramite.id,
						id: dataSubsanacionSujeto.id,
						idTipo: dataSubsanacionSujeto.tipo.id,
					}}
					isOpen={modalSujeto.isShowing}
					onClose={modalSujeto.closeModal}
					onSubmit={onSubmitSubsanacionSujeto}
				/>
			)}
		</>
	);
};

export default TramiteSubsanaciones;
